import { useMemo, useState, type ReactNode } from 'react'
import {
  Badge,
  Card,
  Drawer,
  DrawerSection,
  Empty,
  Field,
  Notice,
  PageHead,
  RowsSkeleton,
  SearchInput,
  Stat,
  Switch,
  Tabs,
} from '../components/ui.js'
import {
  CopyIcon,
  DownloadIcon,
  EyeIcon,
  FilterIcon,
  PlusIcon,
  ReceiptIcon,
  RupeeIcon,
  SparkIcon,
  StoreIcon,
  XIcon,
} from '../components/icons.js'
import { count, dateTime, money } from '../lib/format.js'
import { useDiscounts, type Coupon, type CouponDraft, type CouponStatus } from '../hooks/useDiscounts.js'

/**
 * Coupon codes: create, schedule, limit, and see what each one has earned.
 *
 * The checkout already validates and prices coupons on every totals call; this
 * screen is the only place a merchant can make one. Each row shows orders,
 * revenue and discount given, so "is this code worth running" is answered on
 * the list rather than in a report.
 */

const STATUS: Record<CouponStatus, { label: string; tone: string }> = {
  live: { label: 'Live', tone: 'ok' },
  scheduled: { label: 'Scheduled', tone: 'info' },
  off: { label: 'Off', tone: 'outline' },
  expired: { label: 'Expired', tone: 'warn' },
  used_up: { label: 'Used up', tone: 'warn' },
}

const BLANK: CouponDraft = {
  code: '',
  description: '',
  type: 'percent',
  value: 10,
  minSubtotal: 0,
  maxDiscount: null,
  startsAt: null,
  endsAt: null,
  usageLimit: null,
  perCustomerLimit: 1,
  active: true,
  showAtCheckout: true,
  firstOrderOnly: false,
}

/** Ready-made coupons a merchant can start from — prefilled, never auto-created. */
const SUGGESTED: Array<{ title: string; why: string; draft: CouponDraft }> = [
  {
    title: 'Welcome 10% off',
    why: 'Turns a first-time visitor into a first order. Limited to first orders, so regulars do not use it.',
    draft: { ...BLANK, code: 'WELCOME10', description: 'Get 10% off your first order', value: 10, maxDiscount: 200, firstOrderOnly: true },
  },
  {
    title: 'Flat ₹100 above ₹999',
    why: 'Raises order value: shoppers near ₹999 add one more item to qualify.',
    draft: { ...BLANK, code: 'FLAT100', description: '₹100 off on orders above ₹999', type: 'fixed', value: 100, minSubtotal: 999 },
  },
  {
    title: 'Extra 5% off',
    why: 'A small, always-on nudge for shoppers who hesitate at the payment step.',
    draft: { ...BLANK, code: 'EXTRA5', description: 'Extra 5% off', value: 5, maxDiscount: 150, perCustomerLimit: null },
  },
  {
    title: 'Weekend sale 15%',
    why: 'Time-boxed urgency. Set the dates and it switches itself on and off.',
    draft: { ...BLANK, code: 'WEEKEND15', description: '15% off this weekend', value: 15, maxDiscount: 300 },
  },
  {
    title: 'Win-back ₹150',
    why: 'Hidden from the checkout list — send it in WhatsApp or abandoned-cart messages.',
    draft: { ...BLANK, code: 'COMEBACK150', description: '₹150 off, just for you', type: 'fixed', value: 150, minSubtotal: 799, showAtCheckout: false },
  },
]

type Tab = 'all' | 'suggested'

export function DiscountsPage(): ReactNode {
  const { shop, list, isPending, error, create, update, toggle, duplicate, remove } = useDiscounts()

  const [tab, setTab] = useState<Tab>('all')
  const [search, setSearch] = useState('')
  const [status, setStatus] = useState<CouponStatus | ''>('')
  const [type, setType] = useState<'' | 'percent' | 'fixed'>('')
  const [editing, setEditing] = useState<{ id: string | null; draft: CouponDraft } | null>(null)
  const [rowError, setRowError] = useState<string | null>(null)

  const coupons = list?.coupons ?? []

  const shown = useMemo(() => {
    const needle = search.trim().toUpperCase()

    return coupons.filter(
      (coupon) =>
        (needle === '' || coupon.code.includes(needle) || coupon.description.toUpperCase().includes(needle)) &&
        (status === '' || coupon.status === status) &&
        (type === '' || coupon.type === type),
    )
  }, [coupons, search, status, type])

  const totals = useMemo(
    () =>
      coupons.reduce(
        (sum, coupon) => ({
          orders: sum.orders + coupon.orders,
          revenue: sum.revenue + coupon.revenue,
          discount: sum.discount + coupon.discountGiven,
        }),
        { orders: 0, revenue: 0, discount: 0 },
      ),
    [coupons],
  )

  if (!shop) {
    return (
      <Card>
        <Empty icon={<StoreIcon size={24} />} title="No store selected">
          Coupons belong to a store. Choose one from the switcher above.
        </Empty>
      </Card>
    )
  }

  async function save(draft: CouponDraft): Promise<void> {
    if (editing?.id) await update.mutateAsync({ id: editing.id, body: draft })
    else await create.mutateAsync(draft)

    setEditing(null)
  }

  function act(run: () => Promise<unknown>): void {
    setRowError(null)
    run().catch((cause: unknown) => setRowError(cause instanceof Error ? cause.message : 'That did not work.'))
  }

  const saving = create.isPending || update.isPending
  const formError = (editing?.id ? update.error : create.error)?.message ?? null
  const visible = list?.visible ?? 0
  const limit = list?.visibleLimit ?? 10

  return (
    <>
      <PageHead
        title="Discounts"
        subtitle="Coupon codes for your checkout — and what each one has earned."
        crumbs={[{ label: 'Checkout' }, { label: 'Discounts' }]}
        actions={
          <>
            <button
              type="button"
              className="btn btn--ghost"
              disabled={coupons.length === 0}
              onClick={() => downloadCsv(shown)}
            >
              <DownloadIcon size={15} />
              Download CSV
            </button>
            <button
              type="button"
              className="btn btn--primary"
              onClick={() => {
                create.reset()
                setEditing({ id: null, draft: BLANK })
              }}
            >
              <PlusIcon size={15} />
              Create coupon
            </button>
          </>
        }
      />

      <section className="grid grid--4">
        <Stat
          label="Shown at checkout"
          value={`${visible}/${limit}`}
          icon={<EyeIcon size={20} />}
          tone={visible >= limit ? 'var(--warn)' : 'var(--ok)'}
          footnote={<span className="faint">{Math.max(0, limit - visible)} slots free</span>}
        />
        <Stat
          label="Orders with a coupon"
          value={count(totals.orders)}
          icon={<ReceiptIcon size={20} />}
          footnote={<span className="faint">paid and COD</span>}
        />
        <Stat
          label="Revenue from coupons"
          value={money(totals.revenue)}
          icon={<RupeeIcon size={20} />}
          tone="var(--ok)"
        />
        <Stat
          label="Discount given"
          value={money(totals.discount)}
          icon={<SparkIcon size={20} />}
          tone="var(--accent)"
        />
      </section>

      {rowError && (
        <Notice kind="error" title="Could not update the coupon">
          {rowError}
        </Notice>
      )}

      <Tabs
        tabs={[
          { key: 'all', label: 'All coupons', count: coupons.length },
          { key: 'suggested', label: 'Suggested' },
        ]}
        value={tab}
        onChange={setTab}
      />

      {tab === 'suggested' ? (
        <section className="grid grid--3">
          {SUGGESTED.map((suggestion) => (
            <Card key={suggestion.draft.code} title={suggestion.title} subtitle={suggestion.draft.code}>
              <p className="muted" style={{ marginTop: 0 }}>
                {suggestion.why}
              </p>
              <button
                type="button"
                className="btn btn--sm"
                onClick={() => {
                  create.reset()
                  setEditing({ id: null, draft: suggestion.draft })
                }}
              >
                Use this
              </button>
            </Card>
          ))}
        </section>
      ) : (
        <Card flush>
          <div className="toolbar">
            <SearchInput value={search} onChange={setSearch} placeholder="Search coupon code" />

            <select
              className="select"
              value={status}
              onChange={(event) => setStatus(event.target.value as CouponStatus | '')}
              aria-label="Status"
            >
              <option value="">All statuses</option>
              {Object.entries(STATUS).map(([key, entry]) => (
                <option key={key} value={key}>
                  {entry.label}
                </option>
              ))}
            </select>

            <select
              className="select"
              value={type}
              onChange={(event) => setType(event.target.value as '' | 'percent' | 'fixed')}
              aria-label="Discount type"
            >
              <option value="">All discount types</option>
              <option value="percent">Percentage</option>
              <option value="fixed">Flat amount</option>
            </select>

            <button
              type="button"
              className="btn btn--ghost btn--sm"
              onClick={() => {
                setSearch('')
                setStatus('')
                setType('')
              }}
            >
              <FilterIcon size={15} />
              Clear filters
            </button>
          </div>

          {isPending ? (
            <RowsSkeleton rows={4} />
          ) : error ? (
            <Empty error title="Could not load your coupons" icon={<ReceiptIcon size={20} />}>
              {error.message}
            </Empty>
          ) : coupons.length === 0 ? (
            <Empty
              title="No coupons yet"
              icon={<ReceiptIcon size={20} />}
              action={
                <button type="button" className="btn btn--sm" onClick={() => setTab('suggested')}>
                  See suggested coupons
                </button>
              }
            >
              Create your first code, or start from one of the suggestions.
            </Empty>
          ) : shown.length === 0 ? (
            <Empty title="No coupons match these filters" icon={<FilterIcon size={20} />}>
              Clear the filters to see every coupon.
            </Empty>
          ) : (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Coupon</th>
                    <th>Discount</th>
                    <th>Used</th>
                    <th>Orders</th>
                    <th>Revenue</th>
                    <th>Valid</th>
                    <th>Status</th>
                    <th>On / at checkout</th>
                    <th aria-label="Actions" />
                  </tr>
                </thead>
                <tbody>
                  {shown.map((coupon) => (
                    <tr
                      key={coupon.id}
                      className="is-clickable"
                      onClick={() => {
                        update.reset()
                        setEditing({ id: coupon.id, draft: toDraft(coupon) })
                      }}
                    >
                      <td>
                        <span className="table__cell">
                          <span>
                            <strong className="mono">{coupon.code}</strong>
                            <span className="table__sub">{coupon.description || ' '}</span>
                            <span className="row" style={{ gap: 4, flexWrap: 'wrap', marginTop: 4 }}>
                              {coupon.minSubtotal > 0 && <Badge tone="outline">Min {money(coupon.minSubtotal)}</Badge>}
                              {coupon.firstOrderOnly && <Badge tone="brand">First order</Badge>}
                              {!coupon.showAtCheckout && <Badge tone="outline">Hidden</Badge>}
                            </span>
                          </span>
                        </span>
                      </td>
                      <td>
                        {coupon.label}
                        {coupon.maxDiscount !== null && (
                          <span className="table__sub">up to {money(coupon.maxDiscount)}</span>
                        )}
                      </td>
                      <td className="num">
                        {count(coupon.usedCount)}
                        {coupon.usageLimit !== null && <span className="faint"> / {count(coupon.usageLimit)}</span>}
                      </td>
                      <td className="num">{count(coupon.orders)}</td>
                      <td className="num">{money(coupon.revenue)}</td>
                      <td>
                        <span className="table__sub" style={{ marginTop: 0 }}>
                          {coupon.startsAt ? dateTime(coupon.startsAt) : 'Now'}
                          <br />→ {coupon.endsAt ? dateTime(coupon.endsAt) : 'No end'}
                        </span>
                      </td>
                      <td>
                        <Badge tone={STATUS[coupon.status].tone} dot>
                          {STATUS[coupon.status].label}
                        </Badge>
                      </td>
                      <td onClick={(event) => event.stopPropagation()}>
                        <span className="row" style={{ gap: 10 }}>
                          <Switch
                            label={`${coupon.code} on`}
                            checked={coupon.active}
                            disabled={toggle.isPending}
                            onChange={(next) => act(() => toggle.mutateAsync({ id: coupon.id, body: { active: next } }))}
                          />
                          <Switch
                            label={`Show ${coupon.code} at checkout`}
                            checked={coupon.showAtCheckout}
                            disabled={toggle.isPending}
                            onChange={(next) =>
                              act(() => toggle.mutateAsync({ id: coupon.id, body: { showAtCheckout: next } }))
                            }
                          />
                        </span>
                      </td>
                      <td className="right nowrap" onClick={(event) => event.stopPropagation()}>
                        <button
                          type="button"
                          className="icon-btn"
                          aria-label={`Duplicate ${coupon.code}`}
                          title="Duplicate"
                          onClick={() => act(() => duplicate.mutateAsync(coupon.id))}
                        >
                          <CopyIcon size={16} />
                        </button>
                        <button
                          type="button"
                          className="icon-btn"
                          aria-label={`Delete ${coupon.code}`}
                          title="Delete"
                          onClick={() => {
                            if (confirm(`Delete ${coupon.code}? Shoppers can no longer use it.`)) {
                              act(() => remove.mutateAsync(coupon.id))
                            }
                          }}
                        >
                          <XIcon size={16} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      {editing && (
        <CouponForm
          key={editing.id ?? `new-${editing.draft.code}`}
          initial={editing.draft}
          isNew={editing.id === null}
          saving={saving}
          error={formError}
          onClose={() => setEditing(null)}
          onSave={save}
        />
      )}
    </>
  )
}

function CouponForm({
  initial,
  isNew,
  saving,
  error,
  onClose,
  onSave,
}: {
  initial: CouponDraft
  isNew: boolean
  saving: boolean
  error: string | null
  onClose: () => void
  onSave: (draft: CouponDraft) => Promise<void>
}): ReactNode {
  const [value, setValue] = useState<CouponDraft>(initial)

  function set<K extends keyof CouponDraft>(field: K, next: CouponDraft[K]): void {
    setValue((previous) => ({ ...previous, [field]: next }))
  }

  // The server checks all of these too; here the reason shows beside the form.
  const problem =
    !/^[A-Za-z0-9_-]{2,40}$/.test(value.code)
      ? 'Use 2–40 letters, numbers, - or _ for the code.'
      : !(value.value > 0)
        ? 'Enter a discount above zero.'
        : value.type === 'percent' && value.value > 100
          ? 'A percentage cannot be more than 100.'
          : value.startsAt && value.endsAt && new Date(value.endsAt) <= new Date(value.startsAt)
            ? 'The end must be after the start.'
            : null

  return (
    <Drawer
      open
      onClose={onClose}
      title={isNew ? 'Create coupon' : value.code}
      subtitle="Checked again on every cart, so a code that expires or runs out stops applying on its own."
      footer={
        <>
          {(error || problem) && <span style={{ flex: 1, color: 'var(--bad)', fontSize: '.85rem' }}>{error ?? problem}</span>}
          <button type="button" className="btn btn--ghost" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="btn" disabled={saving || problem !== null} onClick={() => void onSave(value)}>
            {saving ? 'Saving…' : isNew ? 'Create coupon' : 'Save changes'}
          </button>
        </>
      }
    >
      <DrawerSection title="Code">
        <Field label="Coupon code" hint="What the shopper types. Not case-sensitive.">
          <input
            className="input mono"
            value={value.code}
            maxLength={40}
            placeholder="WELCOME10"
            onChange={(event) => set('code', event.target.value.toUpperCase().replace(/[^A-Z0-9_-]/g, ''))}
          />
        </Field>

        <Field label="Description" hint="Shown under the code on the checkout's coupon list.">
          <input
            className="input"
            value={value.description}
            maxLength={190}
            placeholder="Get 10% off your first order"
            onChange={(event) => set('description', event.target.value)}
          />
        </Field>
      </DrawerSection>

      <DrawerSection title="Discount">
        <Field label="Type">
          <select
            className="input"
            value={value.type}
            onChange={(event) => set('type', event.target.value as CouponDraft['type'])}
          >
            <option value="percent">Percentage off</option>
            <option value="fixed">Flat amount off</option>
          </select>
        </Field>

        <Field label={value.type === 'percent' ? 'Percent off' : 'Amount off (₹)'}>
          <input
            type="number"
            className="input"
            min={0}
            max={value.type === 'percent' ? 100 : undefined}
            value={value.value}
            onChange={(event) => set('value', Number(event.target.value) || 0)}
          />
        </Field>

        {value.type === 'percent' && (
          <Field label="Maximum discount (₹)" hint="Caps a percentage on a big cart. Blank for no cap.">
            <input
              type="number"
              className="input"
              min={0}
              value={value.maxDiscount ?? ''}
              onChange={(event) => set('maxDiscount', event.target.value === '' ? null : Number(event.target.value))}
            />
          </Field>
        )}

        <Field label="Minimum order value (₹)" hint="Below it, the checkout says how much more to add.">
          <input
            type="number"
            className="input"
            min={0}
            value={value.minSubtotal}
            onChange={(event) => set('minSubtotal', Math.max(0, Number(event.target.value) || 0))}
          />
        </Field>
      </DrawerSection>

      <DrawerSection title="Who and how often">
        <Field label="Total uses" hint="Across all shoppers. Blank for unlimited.">
          <input
            type="number"
            className="input"
            min={1}
            value={value.usageLimit ?? ''}
            onChange={(event) => set('usageLimit', event.target.value === '' ? null : Math.max(1, Number(event.target.value)))}
          />
        </Field>

        <Field label="Uses per customer" hint="Matched on the verified phone number. Blank for unlimited.">
          <input
            type="number"
            className="input"
            min={1}
            value={value.perCustomerLimit ?? ''}
            onChange={(event) =>
              set('perCustomerLimit', event.target.value === '' ? null : Math.max(1, Number(event.target.value)))
            }
          />
        </Field>

        <Switch
          label="First order only — new customers on this store"
          checked={value.firstOrderOnly}
          onChange={(next) => set('firstOrderOnly', next)}
        />
      </DrawerSection>

      <DrawerSection title="Schedule">
        <Field label="Starts" hint="Leave both blank to run it until you switch it off.">
          <input
            type="datetime-local"
            className="input"
            value={forInput(value.startsAt)}
            onChange={(event) => set('startsAt', fromInput(event.target.value))}
          />
        </Field>

        <Field label="Ends">
          <input
            type="datetime-local"
            className="input"
            value={forInput(value.endsAt)}
            onChange={(event) => set('endsAt', fromInput(event.target.value))}
          />
        </Field>
      </DrawerSection>

      <DrawerSection title="Visibility">
        <Switch label="Coupon is on" checked={value.active} onChange={(next) => set('active', next)} />
        <Switch
          label="Show on the checkout's coupon list"
          checked={value.showAtCheckout}
          onChange={(next) => set('showAtCheckout', next)}
        />
        <p className="muted" style={{ fontSize: '.85rem', margin: 0 }}>
          Hidden coupons still work when typed — useful for influencer codes and recovery messages.
        </p>
      </DrawerSection>
    </Drawer>
  )
}

function toDraft(coupon: Coupon): CouponDraft {
  return {
    code: coupon.code,
    description: coupon.description,
    type: coupon.type,
    value: coupon.value,
    minSubtotal: coupon.minSubtotal,
    maxDiscount: coupon.maxDiscount,
    startsAt: coupon.startsAt,
    endsAt: coupon.endsAt,
    usageLimit: coupon.usageLimit,
    perCustomerLimit: coupon.perCustomerLimit,
    active: coupon.active,
    showAtCheckout: coupon.showAtCheckout,
    firstOrderOnly: coupon.firstOrderOnly,
  }
}

/** ISO → the `datetime-local` value, in the merchant's own time zone. */
function forInput(iso: string | null): string {
  if (!iso) return ''

  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return ''

  const pad = (n: number): string => String(n).padStart(2, '0')

  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`
}

/** The local time the merchant picked, sent as an absolute instant. */
function fromInput(local: string): string | null {
  if (!local) return null
  const date = new Date(local)
  return Number.isNaN(date.getTime()) ? null : date.toISOString()
}

function downloadCsv(coupons: Coupon[]): void {
  const header = [
    'Code', 'Description', 'Type', 'Value', 'Max discount', 'Min order', 'Starts', 'Ends',
    'Usage limit', 'Per customer', 'Used', 'Status', 'On', 'Shown at checkout', 'First order only',
    'Orders', 'Revenue', 'Discount given',
  ]

  const rows = coupons.map((c) => [
    c.code, c.description, c.type, c.value, c.maxDiscount ?? '', c.minSubtotal, c.startsAt ?? '', c.endsAt ?? '',
    c.usageLimit ?? '', c.perCustomerLimit ?? '', c.usedCount, c.status, c.active, c.showAtCheckout, c.firstOrderOnly,
    c.orders, c.revenue, c.discountGiven,
  ])

  const csv = [header, ...rows]
    .map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    .join('\n')

  const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }))
  const link = document.createElement('a')
  link.href = url
  link.download = `coupons-${new Date().toISOString().slice(0, 10)}.csv`
  link.click()
  URL.revokeObjectURL(url)
}
